import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  RelationOne,
  RelationField,
  StringField,
  TextField,
  DecimalField,
  TimestampField,
  JsonField,
  BooleanField,
} from '@ayocore/platform/common/helpers/schema-types';
import partnerSchemaDefinition from './partner.schema';
import dealStatusSchemaDefinition from './deal-status.schema';
import dealTypeSchemaDefinition from './deal-type.schema';
import userSchemaDefinition from '@ayocore/platform/identity/schemas/user.schema';
import accountingCurrencySchemaDefinition from '@ayocore/erp/accounting/schemas/currency.schema';
import lostReasonSchemaDefinition from './lost-reason.schema';

import { name as moduleName } from '../module.json';
/**
 * Схема сделки — основной объект для конвертации t_order.
 */
const DealSchema = Type.Object({
  ...SystemFields,

  number: StringField({
    title: 'Номер сделки',
    nullable: true,
    minLength: 1,
    maxLength: 255,
    description: 'Номер заказа/сделки (legacy number)',
  }),

  description: TextField({
    title: 'Описание',
    nullable: true,
    description: 'Подробное описание сделки',
  }),

  partner: RelationOne(partnerSchemaDefinition),
  partner_id: RelationField(partnerSchemaDefinition, 'ID партнера', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Партнер, связанный со сделкой',
    relationName: 'partner',
  }),

  deal_type: RelationOne(dealTypeSchemaDefinition),
  deal_type_id: RelationField(dealTypeSchemaDefinition, 'ID типа сделки', {
    description: 'Тип сделки',
    relationName: 'deal_type',
  }),

  deal_status: RelationOne(dealStatusSchemaDefinition),
  deal_status_id: RelationField(dealStatusSchemaDefinition, 'ID статуса сделки', {
    description: 'Текущий статус сделки',
    relationName: 'deal_status',
  }),

  amount: DecimalField({
    title: 'Сумма сделки',
    nullable: true,
    precision: 14,
    scale: 2,
    description: 'Сумма сделки (с копейками)',
  }),

  currency: RelationOne(accountingCurrencySchemaDefinition),
  currency_id: RelationField(accountingCurrencySchemaDefinition, 'ID валюты', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Валюта сделки (если отличается от базовой)',
    relationName: 'currency',
  }),

  delivery_date_plan: TimestampField({
    title: 'Плановая дата поставки',
    nullable: true,
    description: 'Плановая дата поставки/исполнения',
  }),

  close_date_fact: TimestampField({
    title: 'Фактическая дата закрытия',
    nullable: true,
    description: 'Фактическая дата закрытия сделки',
  }),

  notes: TextField({
    title: 'Примечания',
    nullable: true,
    description: 'Внутренние заметки по сделке',
  }),

  deal_owner: RelationOne(userSchemaDefinition),
  deal_owner_id: RelationField(userSchemaDefinition, 'ID ответственного', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Ответственный менеджер по сделке',
    relationName: 'deal_owner',
  }),

  archived: BooleanField({
    title: 'В архиве',
    default: false,
    description: 'Признак архивной сделки',
  }),

  archived_at: TimestampField({
    title: 'Дата архивации',
    nullable: true,
    description: 'Дата помещения сделки в архив',
  }),

  archived_by: RelationOne(userSchemaDefinition),
  archived_by_id: RelationField(userSchemaDefinition, 'ID автора архивации', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Пользователь, поместивший сделку в архив',
    relationName: 'archived_by',
  }),

  created_by: RelationOne(userSchemaDefinition),
  created_by_id: RelationField(userSchemaDefinition, 'Кем создана', {
    nullable: true,
    onDelete: 'SET NULL',
    relationName: 'created_by',
  }),

  updated_by: RelationOne(userSchemaDefinition),
  updated_by_id: RelationField(userSchemaDefinition, 'Кем обновлена', {
    nullable: true,
    onDelete: 'SET NULL',
    relationName: 'updated_by',
  }),

  metadata: JsonField(Type.Any(), {
    title: 'Метаданные',
    nullable: true,
    description: 'Дополнительные данные по сделке',
  }),

  // Причина отмены (заполняется при переводе сделки в негативный финальный статус)
  cancel_reason: RelationOne(lostReasonSchemaDefinition),
  cancel_reason_id: RelationField(lostReasonSchemaDefinition, 'Причина отмены', {
    nullable: true,
    onDelete: 'SET NULL',
    description: 'Причина отмены сделки (общий справочник с причинами отказа лидов)',
    relationName: 'cancel_reason',
  }),

  cancel_comment: TextField({
    title: 'Комментарий к причине отмены',
    nullable: true,
    description: 'Свободный комментарий менеджера к причине отмены сделки',
  }),
});

export type Deal = Static<typeof DealSchema>;

export type CreateDeal = Omit<Deal, 'id' | 'uuid' | 'created_at' | 'updated_at' | 'sort_order'>;

export type UpdateDeal = Partial<CreateDeal>;

export default defineSchema({
  moduleName,
  tableName: 'deals', // System will prefix with 'crm_'
  softDelete: true,
  schema: DealSchema,
  additionalIndexes: [
    'number',
    'partner_id',
    'deal_status_id',
    'deal_owner_id',
    'delivery_date_plan',
    'close_date_fact',
    'cancel_reason_id',
  ],
  foreignKeys: [],
  dependencies: [
    'crm_partners',
    'crm_deal_statuses',
    'crm_deal_types',
    'identity_users',
    'accounting_currencies',
    'crm_lost_reasons',
  ],
});
